import React, { useState, useEffect } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Fonctions et non constantes de module : les libellés doivent suivre la langue courante.
const buildDocTypeOptions = (t) => [
  { value: 'purchase_invoice', label: t('tools.docType.purchase_invoice') },
  { value: 'warranty', label: t('tools.docType.warranty') },
  { value: 'user_manual', label: t('tools.docType.user_manual') },
  { value: 'maintenance_record', label: t('tools.docType.maintenance_record') },
  { value: 'calibration_certificate', label: t('tools.docType.calibration_certificate') },
  { value: 'safety_datasheet', label: t('tools.docType.safety_datasheet') },
  { value: 'other', label: t('tools.docType.other') },
];

const buildStatusOptions = (t) => [
  { value: 'valid', label: t('tools.docStatus.valid') },
  { value: 'expired', label: t('tools.docStatus.expired') },
  { value: 'pending', label: t('tools.docStatus.pending') },
];

function ToolDocumentForm({ tool, doc, onClose, onSubmit, onMessage }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    org_id: tool?.org_id || 'ORG001',
    tool_id: tool?.id || '',
    doc_type: 'purchase_invoice',
    doc_number: '',
    issuer: '',
    issue_date: '',
    expiry_date: '',
    file_path: '',
    alert_days_before: 30,
    status: 'valid',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (doc) {
      setFormData({
        org_id: doc.org_id || tool?.org_id || 'ORG001',
        tool_id: doc.tool_id || tool?.id || '',
        doc_type: doc.doc_type || 'purchase_invoice',
        doc_number: doc.doc_number || '',
        issuer: doc.issuer || '',
        issue_date: doc.issue_date || '',
        expiry_date: doc.expiry_date || '',
        file_path: doc.file_path || '',
        alert_days_before: doc.alert_days_before || 30,
        status: doc.status || 'valid',
        notes: doc.notes || '',
      });
    }
  }, [doc, tool]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.doc_type) {
      newErrors.doc_type = t('tools.documentForm.errors.docTypeRequired');
    }

    // Only validate tool_id if we're editing an existing tool document
    // When creating a tool, tool_id will be null and that's okay
    if (!formData.tool_id && tool?.id) {
      newErrors.tool_id = t('tools.documentForm.errors.toolIdRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      onMessage?.(t('tools.documentForm.errors.fixForm'), 'error');
      return;
    }

    try {
      // Prepare data
      const submitData = {
        ...formData,
        tool_id: parseInt(formData.tool_id),
        alert_days_before: parseInt(formData.alert_days_before) || 30,
      };

      await onSubmit(submitData, doc?.id);
    } catch (error) {
      onMessage?.(t('tools.messages.genericError', { error: error.message }), 'error');
    }
  };

  const docTypeOptions = buildDocTypeOptions(t);
  const statusOptions = buildStatusOptions(t);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">
                  {doc ? t('tools.documentForm.editTitle') : t('tools.documentForm.createTitle')}
                </h2>
                <p className="text-amber-100 text-sm">
                  {t('tools.documentForm.forTool', { name: tool?.name || t('common.states.notAvailable') })}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Document Type and Status */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.documentForm.sections.type')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tools.documentForm.fields.docType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="doc_type"
                    value={formData.doc_type}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 ${
                      errors.doc_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  >
                    {docTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.doc_type && (
                    <p className="text-red-500 text-sm mt-1">{errors.doc_type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tools.documentForm.fields.status')}
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Document Details */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.documentForm.sections.details')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tools.documentForm.fields.docNumber')}
                  </label>
                  <input
                    type="text"
                    name="doc_number"
                    value={formData.doc_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder={t('tools.documentForm.placeholders.docNumber')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tools.documentForm.fields.issuer')}
                  </label>
                  <input
                    type="text"
                    name="issuer"
                    value={formData.issuer}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder={t('tools.documentForm.placeholders.issuer')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tools.documentForm.fields.issueDate')}
                  </label>
                  <input
                    type="date"
                    name="issue_date"
                    value={formData.issue_date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                {/* Only show expiry date and alert for non-invoice documents */}
                {formData.doc_type !== 'purchase_invoice' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('tools.documentForm.fields.expiryDate')}
                      </label>
                      <input
                        type="date"
                        name="expiry_date"
                        value={formData.expiry_date}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('tools.documentForm.fields.alertDaysBefore')}
                      </label>
                      <input
                        type="number"
                        name="alert_days_before"
                        value={formData.alert_days_before}
                        onChange={handleChange}
                        min="1"
                        max="365"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                <Upload className="w-5 h-5 inline mr-2" />
                {t('tools.documentForm.sections.file')}
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tools.documentForm.fields.filePath')}
                </label>
                <input
                  type="text"
                  name="file_path"
                  value={formData.file_path}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder={t('tools.documentForm.placeholders.filePath')}
                />
                <p className="text-sm text-gray-500 mt-2">
                  {t('tools.documentForm.hints.filePath')}
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('tools.form.fields.notes')}</h3>
              <div>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder={t('tools.documentForm.placeholders.notes')}
                ></textarea>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
            >
              {doc ? t('tools.form.actions.update') : t('common.actions.add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ToolDocumentForm;
